import { useMemo, useState } from 'react';
import type { Incident } from '../data/demoData';
import { apiFetch, ApiError } from '../lib/apiClient';
import { fmtTs } from '../lib/globalTz';

type WhatIfAction = 'isolate_device' | 'disable_account' | 'block_ioc' | 'revoke_sessions' | 'reset_password' | 'collect_evidence';

interface WhatIfResult {
  ok: true;
  incidentId: string;
  action: WhatIfAction;
  actionLabel: string;
  target: string;
  targetType: string;
  generatedAt: string;
  summary: string;
  expectedContainmentBenefit: { level: 'Low' | 'Medium' | 'High'; detail: string };
  businessImpact: { level: 'Low' | 'Medium' | 'High' | 'Critical'; detail: string };
  dependencyRisk: { level: 'Low' | 'Medium' | 'High' | 'Critical'; detail: string; vipSignals: string[] };
  rollbackPlan: string[];
  requiredPermissions: string[];
  approval: { needed: boolean; level: 'Analyst' | 'SOC Manager' | 'Customer Approval' | 'Emergency Change'; reason: string };
  prerequisites: string[];
  validationSteps: string[];
  aiVerdict: {
    usedAi: boolean;
    model: string;
    verdict: 'Proceed' | 'Proceed with approval' | 'Hold' | 'Investigate further';
    confidence: 'Low' | 'Medium' | 'High';
    summary: string;
    markdown: string;
  };
}

interface Props {
  incident: Incident;
  customerId: string;
}

type IncidentEntity = NonNullable<Incident['entities']>[number];

const ACTIONS: Array<{ value: WhatIfAction; label: string; hint: string }> = [
  { value: 'isolate_device', label: 'Isolate device', hint: 'MDE device isolation / network containment' },
  { value: 'disable_account', label: 'Disable account', hint: 'Entra user/account disable' },
  { value: 'block_ioc', label: 'Block IoC tenant-wide', hint: 'MDE/Sentinel TI block indicator' },
  { value: 'revoke_sessions', label: 'Revoke sessions', hint: 'Invalidate user refresh tokens/sessions' },
  { value: 'reset_password', label: 'Force password reset', hint: 'Identity recovery containment' },
  { value: 'collect_evidence', label: 'Collect evidence only', hint: 'Non-disruptive evidence collection' },
];

function entityName(entity: IncidentEntity): string {
  if (typeof entity === 'string') return entity;
  return String(entity.name || entity.sha256 || '');
}

function entityType(entity: IncidentEntity): string {
  if (typeof entity === 'string') {
    if (entity.includes('@')) return 'User';
    if (/^\d{1,3}(\.\d{1,3}){3}$/.test(entity)) return 'IP';
    if (/^https?:\/\//i.test(entity)) return 'URL';
    if (/^[a-f0-9]{32,64}$/i.test(entity)) return 'File hash';
    return 'Device';
  }
  return String(entity.type || 'Entity');
}

function defaultEntityFor(action: WhatIfAction, incident: Incident): { value: string; type: string } {
  const entities = incident.entities || [];
  const desired = action === 'disable_account' || action === 'revoke_sessions' || action === 'reset_password'
    ? [/account/i, /user/i, /@/]
    : action === 'isolate_device'
      ? [/host/i, /device/i, /machine/i, /computer/i]
      : action === 'block_ioc'
        ? [/ip/i, /url/i, /domain/i, /file/i, /hash/i]
        : [];
  for (const entity of entities) {
    const name = entityName(entity);
    const type = entityType(entity);
    if (!name) continue;
    if (desired.some((rx) => rx.test(type) || rx.test(name))) return { value: name, type };
  }
  const first = entities.map(entityName).find(Boolean);
  return { value: first || incident.title, type: first ? 'Entity' : 'Incident' };
}

function levelClass(level: string): string {
  const v = level.toLowerCase();
  if (v.includes('critical') || v.includes('hold')) return 'bg-red-500/10 text-red-700 dark:text-red-300 border-red-500/30';
  if (v.includes('high') || v.includes('approval')) return 'bg-orange-500/10 text-orange-700 dark:text-orange-300 border-orange-500/30';
  if (v.includes('medium') || v.includes('investigate')) return 'bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30';
  return 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30';
}

function MetricCard({ title, value, detail }: { title: string; value: string; detail: string }) {
  return (
    <div className="rounded-xl border bg-card p-3">
      <div className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">{title}</div>
      <div className={`mt-2 inline-flex rounded-full border px-2.5 py-1 text-xs font-bold ${levelClass(value)}`}>{value}</div>
      <p className="mt-2 text-xs leading-relaxed text-muted-foreground">{detail}</p>
    </div>
  );
}

function ListBlock({ title, items }: { title: string; items: string[] }) {
  return (
    <div className="rounded-xl border bg-background p-3">
      <div className="text-xs font-bold text-foreground">{title}</div>
      <ul className="mt-2 list-disc space-y-1 pl-5 text-xs text-muted-foreground">
        {items.length ? items.map((item, idx) => <li key={`${title}-${idx}`}>{item}</li>) : <li>No items returned.</li>}
      </ul>
    </div>
  );
}

export function IncidentWhatIfPanel({ incident, customerId }: Props) {
  const [action, setAction] = useState<WhatIfAction>('isolate_device');
  const defaultTarget = useMemo(() => defaultEntityFor(action, incident), [action, incident]);
  const [target, setTarget] = useState('');
  const [targetType, setTargetType] = useState('');
  const [analystNote, setAnalystNote] = useState('');
  const [result, setResult] = useState<WhatIfResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const entityOptions = useMemo(() => (incident.entities || [])
    .map((entity) => ({ value: entityName(entity), type: entityType(entity) }))
    .filter((entity) => entity.value), [incident.entities]);

  async function runSimulation() {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch<WhatIfResult>(`/incidents/${encodeURIComponent(incident.armIncidentName || incident.id || incident.incidentNumber)}/what-if`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerId,
          incident,
          action,
          target: target.trim() || defaultTarget.value,
          targetType: targetType.trim() || defaultTarget.type,
          analystNote,
        }),
      });
      setResult(res);
    } catch (e) {
      if (e instanceof ApiError && e.forbidden) setError('Your role is not allowed to run the Incident What-If Simulator. Enable Incidents → Incident What-If Simulator in Permissions Master.');
      else setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="mt-3 rounded-xl border border-purple-200 bg-purple-50/60 p-3 dark:border-purple-900 dark:bg-purple-950/20">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-sm font-bold text-purple-900 dark:text-purple-100">Incident What-If Simulator</p>
          <p className="text-xs text-purple-800/80 dark:text-purple-200/80">
            Simulate disruptive response actions before execution. The AI verdict weighs containment benefit, VIP/entity dependency risk, approval needs and rollback.
          </p>
        </div>
        <span className="rounded-full border border-purple-400/40 bg-purple-500/10 px-2.5 py-1 text-[10px] font-bold uppercase text-purple-700 dark:text-purple-200">
          Decision intelligence
        </span>
      </div>

      <div className="mt-3 grid gap-3 lg:grid-cols-[1.1fr_1fr_1fr]">
        <label className="space-y-1 text-xs font-semibold text-muted-foreground">
          Response action
          <select value={action} onChange={(e) => { setAction(e.target.value as WhatIfAction); setTarget(''); setTargetType(''); }} className="w-full rounded-lg border bg-background px-3 py-2 text-sm text-foreground">
            {ACTIONS.map((a) => <option key={a.value} value={a.value}>{a.label} — {a.hint}</option>)}
          </select>
        </label>
        <label className="space-y-1 text-xs font-semibold text-muted-foreground">
          Target entity
          <input
            value={target || defaultTarget.value}
            onChange={(e) => setTarget(e.target.value)}
            list="ks-what-if-entities"
            className="w-full rounded-lg border bg-background px-3 py-2 text-sm text-foreground"
          />
          <datalist id="ks-what-if-entities">
            {entityOptions.map((entity) => <option key={`${entity.type}:${entity.value}`} value={entity.value}>{entity.type}</option>)}
          </datalist>
        </label>
        <label className="space-y-1 text-xs font-semibold text-muted-foreground">
          Target type
          <input value={targetType || defaultTarget.type} onChange={(e) => setTargetType(e.target.value)} className="w-full rounded-lg border bg-background px-3 py-2 text-sm text-foreground" />
        </label>
      </div>

      <label className="mt-3 block space-y-1 text-xs font-semibold text-muted-foreground">
        Analyst note / business context <span className="font-normal">(optional)</span>
        <textarea
          value={analystNote}
          onChange={(e) => setAnalystNote(e.target.value)}
          placeholder="Example: Target belongs to Finance VIP, device is used for payment processing, customer asked to avoid account disable during business hours..."
          className="min-h-[70px] w-full rounded-lg border bg-background px-3 py-2 text-sm text-foreground"
        />
      </label>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button type="button" onClick={() => void runSimulation()} disabled={loading} className="rounded-lg bg-purple-600 px-4 py-2 text-sm font-bold text-white hover:bg-purple-700 disabled:opacity-60">
          {loading ? 'Simulating…' : 'Run What-If + AI Verdict'}
        </button>
        {result && <span className="text-xs text-muted-foreground">Generated {fmtTs(result.generatedAt)}</span>}
      </div>

      {error && <div className="mt-3 rounded-lg border border-red-300 bg-red-50 p-3 text-xs text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-200">{error}</div>}

      {result && (
        <div className="mt-4 space-y-4">
          <div className="rounded-xl border bg-card p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">AI Verdict</div>
                <div className={`mt-2 inline-flex rounded-full border px-3 py-1.5 text-sm font-extrabold ${levelClass(result.aiVerdict.verdict)}`}>{result.aiVerdict.verdict}</div>
                <p className="mt-2 text-sm leading-relaxed text-foreground">{result.aiVerdict.summary}</p>
              </div>
              <div className="text-right text-[11px] text-muted-foreground">
                <div>{result.aiVerdict.usedAi ? `Azure OpenAI · ${result.aiVerdict.model}` : 'Deterministic fallback'}</div>
                <div>Confidence: <span className="font-semibold text-foreground">{result.aiVerdict.confidence}</span></div>
                <div>Approval: <span className="font-semibold text-foreground">{result.approval.needed ? result.approval.level : 'Not required'}</span></div>
              </div>
            </div>
            <pre className="mt-3 max-h-[220px] overflow-y-auto whitespace-pre-wrap rounded-lg border bg-muted/30 p-3 text-xs text-muted-foreground">{result.aiVerdict.markdown}</pre>
          </div>

          <div className="grid gap-3 md:grid-cols-3">
            <MetricCard title="Containment benefit" value={result.expectedContainmentBenefit.level} detail={result.expectedContainmentBenefit.detail} />
            <MetricCard title="Business impact" value={result.businessImpact.level} detail={result.businessImpact.detail} />
            <MetricCard title="Dependency risk" value={result.dependencyRisk.level} detail={result.dependencyRisk.detail} />
          </div>

          <div className="rounded-xl border bg-card p-3">
            <div className="text-xs font-bold text-foreground">Approval Decision</div>
            <p className="mt-2 text-sm text-foreground">
              <span className={`mr-2 rounded-full border px-2.5 py-1 text-xs font-bold ${levelClass(result.approval.level)}`}>{result.approval.needed ? result.approval.level : 'No approval required'}</span>
              {result.approval.reason}
            </p>
            {result.dependencyRisk.vipSignals.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {result.dependencyRisk.vipSignals.map((signal) => <span key={signal} className="rounded-full border border-red-500/30 bg-red-500/10 px-2 py-1 text-[11px] font-semibold text-red-700 dark:text-red-300">{signal}</span>)}
              </div>
            )}
          </div>

          <div className="grid gap-3 lg:grid-cols-2">
            <ListBlock title="Required permissions" items={result.requiredPermissions} />
            <ListBlock title="Rollback plan" items={result.rollbackPlan} />
            <ListBlock title="Pre-checks" items={result.prerequisites} />
            <ListBlock title="Post-action validation" items={result.validationSteps} />
          </div>
        </div>
      )}
    </div>
  );
}
